import * as Sentry from '@sentry/nextjs';

type ErrorLogger = (message: string, error: Error) => void;

// Per-runtime latch. Server and browser load this module
// once each and patch their respective copy of the
// PostgrestBuilder prototype.
let done = false;

interface PostgrestThenContext {
  url?: URL;
  method?: string;
}

const WRAPPED_MARKER = Symbol.for('clai.sentry.postgrest.wrapped');

interface PatchableProto {
  then?: (...args: unknown[]) => unknown;
  [WRAPPED_MARKER]?: true;
}

/**
 * Emits a `db.supabase.<table>.<op>` Sentry span for every
 * `await client.from(...).<op>(...)` call.
 *
 * Locates `PostgrestBuilder.prototype` by walking a live
 * builder's prototype chain. Turbopack can load multiple
 * copies of `@supabase/postgrest-js` per runtime, and
 * patching one imported reference would miss prototypes
 * inherited by builders from a different copy.
 *
 * `.rpc()` and storage requests are intentionally not
 * wrapped here — they emit the http.client spans from the
 * outbound fetch instrumentation.
 */
export function instrumentSupabasePrototypeOnce(
  client: object,
  onError: ErrorLogger
): void {
  if (done) return;
  try {
    // Walk: client.from('__probe__').select('*') yields
    // a PostgrestFilterBuilder; walk its prototype chain
    // up to the class that owns `.then`.
    const builder = (client as { from?: (table: string) => unknown }).from?.(
      '__sentry_probe__'
    );
    if (!builder || typeof builder !== 'object') {
      done = true;
      return;
    }
    const selectFn = (builder as { select?: (col: string) => unknown }).select;
    const thenable =
      typeof selectFn === 'function' ? selectFn.call(builder, '*') : builder;

    let proto: PatchableProto | null = Object.getPrototypeOf(thenable);
    while (
      proto &&
      !Object.prototype.hasOwnProperty.call(proto, 'then') &&
      Object.getPrototypeOf(proto)
    ) {
      proto = Object.getPrototypeOf(proto) as PatchableProto;
    }

    if (!proto || typeof proto.then !== 'function') {
      done = true;
      return;
    }
    if (proto[WRAPPED_MARKER]) {
      done = true;
      return;
    }

    const originalThen = proto.then;
    proto[WRAPPED_MARKER] = true;
    proto.then = function patchedThen(
      this: PostgrestThenContext,
      onFulfilled?: unknown,
      onRejected?: unknown
    ) {
      const path = this.url?.pathname ?? '';
      const segments = path.split('/').filter(Boolean);
      // PostgREST table query: /rest/v{N}/<table>
      // PostgREST RPC call:    /rest/v{N}/rpc/<fn>
      const isRpc = segments[2] === 'rpc';
      if (isRpc) {
        // RPC kept as plain http.client span — same as before.
        return originalThen.call(this, onFulfilled, onRejected);
      }
      const table = segments[2] ?? 'unknown';
      const op = (this.method ?? 'GET').toLowerCase();
      return Sentry.startSpan(
        {
          op: `db.supabase.${table}.${op}`,
          name: `${op.toUpperCase()} ${table}`,
        },
        () => originalThen.call(this, onFulfilled, onRejected)
      );
    };
    done = true;
  } catch (err) {
    onError(
      'Failed to instrument PostgrestBuilder for Sentry',
      err instanceof Error ? err : new Error(String(err))
    );
  }
}
